import { useState, useCallback, useEffect } from 'react';
import { showErrorNotification, showNotification } from '../../b2b-common/Notification';
import { createLogbook, deleteLogbook } from '../../../services/b2b-logbook-apiServices';
import useAccessControl from '../../b2b-users/hooks/useAccessControl';

const getEntityId = (logbookType, entity) => {
    if (!entity) {
        return null;
    }

    switch (logbookType) {
        case 'PLAN':
            return entity.planUUID;
        case 'AREA':
            return entity.areaId;
        case 'AREA_INDICATOR':
            return entity.areaIndicatorId;
        default:
            return null;
    }
};

const useLogbook = (logbookType, loginData, setNotification, t, onClose, logbookRelatedEntity, newLogbookDTO, fetchLogbookFn) => {
    const [currentOpen, setCurrentOpen] = useState(false);
    const [currentEntity, setCurrentEntity] = useState(null);
    const [currentLogbook, setCurrentLogbook] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [newLogbookConfirmationRequest, setNewLogbookConfirmationRequest] = useState(false);
    const [fetchingLogbook, setFetchingLogbook] = useState(false);
    const [logbookDeleteConfirmationRequest, setLogbookDeleteConfirmationRequest] = useState(false);
    const { checkAccess: createLogbookAccess } = useAccessControl([{
        productsRequired: ['B2B_LOGBOOK'],
        rolesRequired: ['ADMIN', 'EDITOR']
    }])

    const createNewLogbook = async (entity) => {
        if (!createLogbookAccess()) {
            showNotification(setNotification, t('You do not have permission to create a new logbook.'), 'error', 5000);
            return null;
        }
        try {
            const newLogbook = newLogbookDTO(loginData, entity);
            const createdLogbook = await createLogbook(loginData.accessToken, newLogbook);
            showNotification(setNotification, t('New logbook created successfully.'), 'success', 3000);
            return createdLogbook;
        } catch (error) {
            console.error('Error creating new logbook:', error);
            showNotification(setNotification, t('Error creating new logbook: {{error}}', { error: error.response.statusText }), 'error', 5000);
        }
    };

    const handleClose = useCallback(() => {
        setCurrentEntity(null);
        setCurrentLogbook(null);
        setCurrentOpen(false);
        onClose();
    }, [onClose]);

    const getLogbook = useCallback(async (entity) => {
        if (!fetchingLogbook) {
            try {
                const id = getEntityId(logbookType, entity);
                setFetchingLogbook(true);
                const logbook = await fetchLogbookFn(loginData.accessToken, id);
                return logbook;
            } catch (error) {
                console.error('Error fetching logbook:', error);
                if (error.response && error.response.status === 404) {
                    setNewLogbookConfirmationRequest(true);
                } else {
                    showErrorNotification(setNotification, t('Error fetching logbook'), error, 50000);
                    // const errorMessage = error.response?.data?.Message || t('Unknown error');
                    // const errorDetails = error.response?.data?.Details || '';
                    // showNotification(
                    //     setNotification,
                    //     t('Error fetching logbook: {{error}}', { error: `${errorMessage} ${errorDetails}` }),
                    //     'error',
                    //     5000
                    // );
                }
                handleClose();
                return null;
            } finally {
                setFetchingLogbook(false);
            }
        }
    }, [fetchingLogbook, logbookType, fetchLogbookFn, loginData.accessToken, handleClose, setNotification, t]);

    useEffect(() => {
        const fetchLogbook = async () => {
            if (!currentOpen) {
                setCurrentOpen(false);
                setCurrentEntity(null);
                setCurrentLogbook(null);
                return;
            }
            const logbookRelatedEntity_id = getEntityId(logbookType, logbookRelatedEntity);
            const currentEntity_id = getEntityId(logbookType, currentEntity);
            if (
                (logbookRelatedEntity && logbookRelatedEntity_id !== currentEntity_id)
                ||
                (!currentLogbook)
            ) {
                setCurrentEntity(logbookRelatedEntity);
                const logbookData = await getLogbook(logbookRelatedEntity);
                setCurrentLogbook(logbookData);
                setCurrentOpen(!!logbookData);
            }
        };
        fetchLogbook();
    }, [currentOpen]);
    // }, [currentOpen, currentLogbook, getLogbook, logbookRelatedEntity, logbookType, currentEntity]);

    const handleCreateNewLogbook = async () => {
        const newLogbook = await createNewLogbook(logbookRelatedEntity);
        setCurrentLogbook(newLogbook);
        setNewLogbookConfirmationRequest(false);
    };

    const handleUpdatedLogbook = (updatedLogbook) => {
        setCurrentLogbook(updatedLogbook);
    };

    const handleFolderChange = (folder) => {
        setCurrentFolder(folder);
    };

    const handleDeleteLogbook = async () => {
        try {
            await deleteLogbook(loginData.accessToken, currentLogbook.logBookId);
            showNotification(setNotification, t('Logbook deleted successfully.'), 'success', 3000);
            setCurrentLogbook(null);
            setLogbookDeleteConfirmationRequest(false);
            handleClose();
        } catch (error) {
            console.error('Error deleting logbook:', error);
            showNotification(setNotification, t('Error deleting logbook: {{error}}', { error: error.response.statusText }), 'error', 5000);
        }
    };

    return {
        currentOpen,
        setCurrentOpen,
        currentLogbook,
        setCurrentLogbook,
        currentFolder,
        newLogbookConfirmationRequest,
        setNewLogbookConfirmationRequest,
        logbookDeleteConfirmationRequest,
        setLogbookDeleteConfirmationRequest,
        handleCreateNewLogbook,
        handleUpdatedLogbook,
        handleFolderChange,
        handleDeleteLogbook,
        handleClose,
    };
};

export default useLogbook;